// Thương hiệu riêng của NPP (docs/KE_HOACH_THUONG_HIEU_NPP.md): bộ màu + logo áp cho cả cây.
// NPP chọn màu chính (bắt buộc), màu thanh menu và màu nền trang (tuỳ chọn). Hàm ở đây tự suy ra các biến
// màu của web (--accent, --accent-soft, --band…) cho chế độ sáng + tối và tự chỉnh màu cho chữ đủ dễ đọc
// (tương phản ≥ 4,5 : 1). Màu trạng thái (--ok, --danger, --warn…) KHÔNG theo NPP — giữ nghĩa tiền / đơn.
// Dùng chung cho web (áp lúc chạy), màn xem trước và máy chủ (kiểm khi lưu).

export type Rgb = [number, number, number]

export interface BrandColors {
  /** Màu chính — nút, liên kết, mục đang chọn. "#rrggbb". */
  primary: string
  /** Nền thanh bên / thanh trên / thanh tab (chế độ sáng). null = trắng như mặc định. */
  menu?: string | null
  /** Nền trang (chế độ sáng). null = mặc định. */
  background?: string | null
}

/** Thương hiệu NPP gửi cho web — không có gì nhạy cảm, ai cũng xem được. */
export interface BrandInfo {
  nppId: string
  /** Tên NPP hiện thay chữ ShopTrack (Npp.displayName) */
  name: string
  /** null = NPP chưa đặt màu → giữ màu mặc định (kể cả màu theo vai trò) */
  colors: BrandColors | null
  /** Đường dẫn ảnh logo (có ?v= theo nội dung) — null = chưa tải lên */
  logoWide: string | null
  logoSquare: string | null
  /** Rộng / cao của logo ngang — để giữ chỗ đúng tỉ lệ trước khi ảnh tải xong */
  logoWideRatio: number | null
}

/** Thương hiệu trong email (mã đăng nhập, đặt lại mật khẩu) — null = ShopTrack như cũ. */
export interface EmailBrand {
  name: string
  /** Màu chính đã chỉnh cho dễ đọc trên nền trắng ("#rrggbb") */
  color: string
  /** Đường dẫn tuyệt đối tới logo (ngang, không có thì vuông) */
  logoUrl: string | null
}

export const BRAND_LOGO_KINDS = ['wide', 'square'] as const
export type BrandLogoKind = (typeof BRAND_LOGO_KINDS)[number]

/** Kích thước logo sau khi trình duyệt chuẩn hoá (PNG) — máy chủ kiểm lại đúng các giới hạn này. */
export const BRAND_LOGO_RULES = {
  /** Byte tối đa của file đã chuẩn hoá */
  maxBytes: 200 * 1024,
  /** Byte tối đa của file gốc người dùng chọn */
  maxSourceBytes: 2 * 1024 * 1024,
  wide: { minHeight: 64, maxHeight: 128, minRatio: 1, maxRatio: 4 },
  square: { minSize: 64, maxSize: 256 },
} as const

/** Bộ màu mẫu để chọn nhanh — đều đạt tương phản chữ trắng ≥ 4,5 : 1. */
export const BRAND_PRESETS: { key: string; label: string; colors: BrandColors }[] = [
  { key: 'blue', label: 'Xanh dương', colors: { primary: '#2c63e0' } },
  { key: 'green', label: 'Xanh lục', colors: { primary: '#047857', background: '#f3f8f5' } },
  { key: 'teal', label: 'Xanh ngọc', colors: { primary: '#0e7490', background: '#f2f8fa' } },
  { key: 'red', label: 'Đỏ', colors: { primary: '#c0262d', background: '#fbf5f4' } },
  { key: 'orange', label: 'Cam', colors: { primary: '#b45309', background: '#fbf7f1' } },
  { key: 'pink', label: 'Hồng', colors: { primary: '#be185d', background: '#fcf4f7' } },
  { key: 'violet', label: 'Tím', colors: { primary: '#6d28d9', background: '#f7f5fd' } },
  { key: 'slate', label: 'Than chì', colors: { primary: '#334155', menu: '#f1f4f8' } },
]

// Màu nền / chữ mặc định của web (apps/web/src/index.css) — để kiểm tương phản
const WHITE: Rgb = [255, 255, 255]
const PAPER: Rgb = [244, 247, 252]
const INK: Rgb = [27, 36, 55]
const MUTED: Rgb = [95, 107, 133]
const DARK_PAPER: Rgb = [15, 21, 32]
const DARK_RAISE: Rgb = [22, 30, 43]
const DARK_ON_ACCENT: Rgb = [14, 21, 34]

/** Tương phản tối thiểu cho chữ thường (WCAG AA) */
export const MIN_TEXT_CONTRAST = 4.5

const HEX_RE = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i

/** "#1a6fe0" | "1a6fe0" | "#abc" → [r, g, b]; sai định dạng → null */
export function parseHex(input: string | null | undefined): Rgb | null {
  if (!input) return null
  const m = HEX_RE.exec(input.trim())
  if (!m) return null
  let h = m[1]!
  if (h.length === 3) h = h.split('').map((c) => c + c).join('')
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)]
}

export function toHex([r, g, b]: Rgb): string {
  return '#' + [r, g, b].map((v) => Math.round(v).toString(16).padStart(2, '0')).join('')
}

/** Chuẩn hoá về "#rrggbb" chữ thường; sai → null */
export function normalizeHex(input: string | null | undefined): string | null {
  const c = parseHex(input)
  return c ? toHex(c) : null
}

function channel(v: number): number {
  const s = v / 255
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
}

export function luminance([r, g, b]: Rgb): number {
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

export function contrast(a: Rgb, b: Rgb): number {
  const la = luminance(a)
  const lb = luminance(b)
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

function rgbToHsl([r, g, b]: Rgb): [number, number, number] {
  const rn = r / 255, gn = g / 255, bn = b / 255
  const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn)
  const l = (max + min) / 2
  if (max === min) return [0, 0, l]
  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  let h = max === rn ? (gn - bn) / d + (gn < bn ? 6 : 0) : max === gn ? (bn - rn) / d + 2 : (rn - gn) / d + 4
  h /= 6
  return [h, s, l]
}

function hslToRgb([h, s, l]: [number, number, number]): Rgb {
  if (s === 0) return [l * 255, l * 255, l * 255].map(Math.round) as Rgb
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q
  const hue = (t: number) => {
    if (t < 0) t += 1
    if (t > 1) t -= 1
    if (t < 1 / 6) return p + (q - p) * 6 * t
    if (t < 1 / 2) return q
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
    return p
  }
  return [hue(h + 1 / 3), hue(h), hue(h - 1 / 3)].map((v) => Math.round(v * 255)) as Rgb
}

/** Trộn a với b: t = 0 → a, t = 1 → b */
export function mix(a: Rgb, b: Rgb, t: number): Rgb {
  return [0, 1, 2].map((i) => Math.round(a[i]! + (b[i]! - a[i]!) * t)) as Rgb
}

/**
 * Đổi độ sáng (giữ sắc độ) theo hướng `dir` tới khi `ok(màu)` đúng. Dừng ở đen / trắng.
 * Trả màu gốc nếu đã đạt.
 */
function shiftUntil(c: Rgb, dir: -1 | 1, ok: (x: Rgb) => boolean): Rgb {
  if (ok(c)) return c
  const [h, s, l0] = rgbToHsl(c)
  for (let l = l0; dir < 0 ? l > 0 : l < 1; l += dir * 0.005) {
    const x = hslToRgb([h, s, Math.min(1, Math.max(0, l))])
    if (ok(x)) return x
  }
  return dir < 0 ? [0, 0, 0] : [255, 255, 255]
}

function shiftLightness(c: Rgb, delta: number): Rgb {
  const [h, s, l] = rgbToHsl(c)
  return hslToRgb([h, s, Math.min(1, Math.max(0, l + delta))])
}

const sameRgb = (a: Rgb, b: Rgb) => a[0] === b[0] && a[1] === b[1] && a[2] === b[2]

/** Kết quả suy bộ màu: biến CSS ("r g b") cho chế độ sáng + tối, và màu nào đã bị chỉnh cho dễ đọc. */
export interface BrandPalette {
  light: Record<string, string>
  dark: Record<string, string>
  /** Màu thật dùng sau khi chỉnh (hex) — để màn xem trước báo "đã chỉnh" */
  used: { primary: string; menu: string | null; background: string | null }
  adjusted: { primary: boolean; menu: boolean; background: boolean }
}

const v = (c: Rgb) => `${c[0]} ${c[1]} ${c[2]}`

/** Nền sáng đủ cho chữ phụ (màu muted) đạt ≥ 4,5 : 1 và màu chính đạt ≥ 3 : 1 (viền, vạch chọn). */
function lightSurface(c: Rgb, accent: Rgb): Rgb {
  return shiftUntil(c, 1, (x) => contrast(MUTED, x) >= MIN_TEXT_CONTRAST && contrast(INK, x) >= 7 && contrast(accent, x) >= 3)
}

/**
 * Bộ màu của NPP → biến CSS. Màu chính quá nhạt (chữ trắng trên nút, chữ màu trên nền khó đọc) thì
 * làm đậm dần tới khi đạt; nền / menu quá đậm thì làm nhạt dần. Sai định dạng màu chính → null.
 */
export function brandPalette(colors: BrandColors): BrandPalette | null {
  const primary = parseHex(colors.primary)
  if (!primary) return null
  const menuIn = parseHex(colors.menu ?? null)
  const bgIn = parseHex(colors.background ?? null)

  // Nền trang thật (để kiểm chữ màu chính đặt trên nền)
  const paperGuess = bgIn ?? PAPER
  const accent = shiftUntil(primary, -1, (x) =>
    contrast(WHITE, x) >= MIN_TEXT_CONTRAST && contrast(x, paperGuess) >= MIN_TEXT_CONTRAST && contrast(x, menuIn ?? WHITE) >= MIN_TEXT_CONTRAST,
  )
  const background = bgIn ? lightSurface(bgIn, accent) : null
  const menu = menuIn ? lightSurface(menuIn, accent) : null
  const paper = background ?? PAPER
  // Màu chính phải đạt trên nền ĐÃ chỉnh (nền chỉ sáng lên nên thường vẫn đạt — kiểm lại cho chắc)
  const accentFinal = shiftUntil(accent, -1, (x) => contrast(x, paper) >= MIN_TEXT_CONTRAST && contrast(x, menu ?? WHITE) >= MIN_TEXT_CONTRAST && contrast(WHITE, x) >= MIN_TEXT_CONTRAST)
  const accentSoft = shiftLightness(accentFinal, -0.07)

  const light: Record<string, string> = {
    '--accent': v(accentFinal),
    '--accent-soft': v(accentSoft),
    '--on-accent': v(WHITE),
    '--band': v(mix(accentFinal, paper, 0.88)),
    '--role': v(accentFinal),
  }
  if (background) {
    light['--paper'] = v(background)
    light['--paper-soft'] = v(mix(background, INK, 0.035))
    light['--rule'] = v(mix(background, INK, 0.09))
  }
  if (menu) light['--brand-menu'] = v(menu)

  // Chế độ tối: làm sáng màu chính tới khi đọc được trên nền tối và chữ tối trên nút đọc được
  const darkAccent = shiftUntil(primary, 1, (x) =>
    contrast(x, DARK_PAPER) >= MIN_TEXT_CONTRAST && contrast(x, DARK_RAISE) >= MIN_TEXT_CONTRAST && contrast(DARK_ON_ACCENT, x) >= MIN_TEXT_CONTRAST,
  )
  const dark: Record<string, string> = {
    '--accent': v(darkAccent),
    '--accent-soft': v(shiftLightness(darkAccent, 0.06)),
    '--on-accent': v(DARK_ON_ACCENT),
    '--band': v(mix(darkAccent, DARK_PAPER, 0.82)),
    '--role': v(darkAccent),
  }

  return {
    light,
    dark,
    used: { primary: toHex(accentFinal), menu: menu ? toHex(menu) : null, background: background ? toHex(background) : null },
    adjusted: {
      primary: !sameRgb(accentFinal, primary),
      menu: !!menuIn && !!menu && !sameRgb(menu, menuIn),
      background: !!bgIn && !!background && !sameRgb(background, bgIn),
    },
  }
}

function cssBlock(selector: string, vars: Record<string, string>, extra = ''): string {
  const body = Object.entries(vars).map(([k, val]) => `${k}:${val} !important;`).join('')
  return `${selector}{${body}${extra}}`
}

/**
 * CSS biến màu của bộ màu NPP (sáng: `${scope}:not(.dark)`, tối: `${scope}.dark`), !important để thắng màu theo
 * vai trò / Glass / màu dịp. Web áp ở <html data-npp-brand>; màn xem trước áp vào khung xem trước.
 */
export function brandCss(colors: BrandColors, scope = ':root[data-npp-brand]'): string {
  const pal = brandPalette(colors)
  if (!pal) return ''
  // Glass điện thoại lấy màu nút / chữ nhấn từ --g-cta-*, --g-on; có màu menu thì kính thanh trên nhuốm màu menu
  const glass = '--g-cta-1:var(--accent) !important;--g-cta-2:var(--accent) !important;--g-on:var(--accent) !important;'
  const tint = pal.light['--brand-menu'] ? '--g-tint:rgb(var(--brand-menu) / .8) !important;' : ''
  return [cssBlock(`${scope}:not(.dark)`, pal.light, glass + tint), cssBlock(`${scope}.dark`, pal.dark, glass)].join('\n')
}

const esc = (x: string) => x.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;')

/** Đầu email: logo NPP (cao 40px, rộng tối đa 200px) hoặc tên NPP; không có thương hiệu → chữ ShopTrack. HTML đã thoát ký tự. */
export function emailBrandHeader(brand: EmailBrand | null): string {
  if (!brand) return 'ShopTrack'
  if (brand.logoUrl) {
    return `<img src="${esc(brand.logoUrl)}" alt="${esc(brand.name)}" height="40" style="display:block;height:40px;width:auto;max-width:200px;border:0;">`
  }
  return esc(brand.name)
}

/** Chữ cái đầu của tên NPP (logo dự phòng khi chưa có / ảnh lỗi) — bỏ dấu tiếng Việt cho gọn */
export function brandInitial(name: string): string {
  const word = name.trim().split(/\s+/)[0] ?? ''
  const ch = word.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/gi, 'D').charAt(0)
  return (ch || 'S').toUpperCase()
}

/** Đọc rộng / cao từ đầu file PNG (khối IHDR). Không phải PNG → null. */
export function pngSize(buf: Uint8Array): { width: number; height: number } | null {
  const sig = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]
  if (buf.length < 24 || sig.some((b, i) => buf[i] !== b)) return null
  // Khối đầu tiên phải là IHDR (byte 12–15)
  if (String.fromCharCode(buf[12]!, buf[13]!, buf[14]!, buf[15]!) !== 'IHDR') return null
  const u32 = (o: number) => ((buf[o]! << 24) | (buf[o + 1]! << 16) | (buf[o + 2]! << 8) | buf[o + 3]!) >>> 0
  return { width: u32(16), height: u32(20) }
}

/** Kiểm logo đã chuẩn hoá. Trả câu báo lỗi tiếng Việt, hoặc null nếu đạt. */
export function checkBrandLogo(kind: BrandLogoKind, buf: Uint8Array): string | null {
  if (buf.length > BRAND_LOGO_RULES.maxBytes) return `Logo nặng quá ${Math.round(BRAND_LOGO_RULES.maxBytes / 1024)} KB sau khi thu nhỏ — chọn ảnh đơn giản hơn`
  const size = pngSize(buf)
  if (!size) return 'File logo không phải ảnh PNG hợp lệ'
  const { width, height } = size
  if (kind === 'wide') {
    const r = BRAND_LOGO_RULES.wide
    if (height < r.minHeight) return `Logo ngang quá nhỏ: cao tối thiểu ${r.minHeight} px`
    if (height > r.maxHeight) return `Logo ngang cao tối đa ${r.maxHeight} px`
    const ratio = width / height
    if (ratio < r.minRatio) return 'Logo ngang phải rộng hơn hoặc bằng chiều cao — ảnh đứng hãy dùng làm logo vuông'
    if (ratio > r.maxRatio) return `Logo ngang dài quá: rộng tối đa ${r.maxRatio} lần chiều cao`
    return null
  }
  const r = BRAND_LOGO_RULES.square
  if (width !== height) return 'Logo vuông phải có rộng bằng cao'
  if (width < r.minSize) return `Logo vuông quá nhỏ: tối thiểu ${r.minSize} × ${r.minSize} px`
  if (width > r.maxSize) return `Logo vuông tối đa ${r.maxSize} × ${r.maxSize} px`
  return null
}
